import React, { useCallback, useEffect, useMemo, useState } from "react";
import { authApi, getApiErrorMessage } from "../api";
import { CheckCircle2, Eye, Loader2, RefreshCw, Search, Send } from "lucide-react";
import LogisticsModal from "./LogisticsModal";

const S = `
  .dq-wrap { padding: 24px; font-family: 'Inter', sans-serif; color: #0f172a; }
  .dq-head { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
  .dq-title { margin:0; font-size:24px; font-weight:800; color:#0f172a; }
  .dq-sub { font-size:13px; color:#475569; margin-top:4px; }
  .dq-tools { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
  .dq-search { position:relative; min-width:260px; flex:1; }
  .dq-search input, .dq-select, .dq-textarea { width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:12px; padding:10px 12px; font-size:13px; background:#fff; color:#0f172a; }
  .dq-search input { padding-left:34px; }
  .dq-btn { border:1px solid #cbd5e1; border-radius:10px; background:#fff; color:#0f172a; padding:9px 12px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }
  .dq-btn:disabled { opacity:.6; cursor:not-allowed; }
  .dq-btn-primary { background:#0f172a; border-color:#0f172a; color:#fff; }
  .dq-btn-success { background:#15803d; border-color:#15803d; color:#fff; }
  .dq-btn-warning { background:#d97706; border-color:#d97706; color:#fff; }
  .dq-table-wrap { background:#fff; border:1px solid #e2e8f0; border-radius:16px; overflow:auto; box-shadow:0 8px 20px rgba(15,23,42,.04); }
  .dq-table { width:100%; min-width:1100px; border-collapse:collapse; }
  .dq-table th { text-align:left; padding:12px; font-size:11px; color:#475569; border-bottom:1px solid #e2e8f0; text-transform:uppercase; letter-spacing:.5px; }
  .dq-table td { padding:13px 12px; font-size:13px; color:#0f172a; border-bottom:1px solid #f1f5f9; vertical-align:top; }
  .dq-badge { display:inline-flex; align-items:center; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:700; }
  .dq-empty { padding:56px 12px; text-align:center; color:#64748b; }
  .dq-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:1200; display:flex; justify-content:center; align-items:center; padding:18px; }
  .dq-modal { width:min(920px, 100%); background:#fff; border-radius:18px; box-shadow:0 18px 40px rgba(15,23,42,.24); overflow:hidden; }
  .dq-modal-head { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:16px 18px; border-bottom:1px solid #e2e8f0; }
  .dq-modal-body { padding:18px; display:grid; gap:16px; max-height:78vh; overflow:auto; }
  .dq-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:14px; }
  .dq-card { border:1px solid #e2e8f0; border-radius:14px; padding:14px; background:#f8fafc; }
  .dq-card h4 { margin:0 0 10px; font-size:13px; font-weight:800; color:#0f172a; }
  .dq-row { display:flex; justify-content:space-between; gap:10px; padding:5px 0; font-size:13px; }
  .dq-row span:first-child { color:#64748b; font-weight:600; }
  .dq-actions { display:flex; gap:8px; flex-wrap:wrap; margin-top:8px; }
  .dq-notes { display:grid; gap:8px; }
  .dq-inline-label { display:block; font-size:12px; font-weight:700; color:#334155; margin-bottom:6px; }
  @media (max-width: 768px) { .dq-wrap { padding: 16px; } .dq-grid { grid-template-columns:1fr; } }
`;

const badgeStyle = (status) => {
  switch (status) {
    case "PENDING":
      return { background: "#fef3c7", color: "#92400e" };
    case "APPROVED":
    case "DISPATCH_APPROVED":
      return { background: "#dcfce7", color: "#166534" };
    case "DISPATCHED":
      return { background: "#dbeafe", color: "#1d4ed8" };
    case "PARTIALLY_DISPATCHED":
      return { background: "#ffedd5", color: "#c2410c" };
    case "REJECTED":
      return { background: "#fee2e2", color: "#991b1b" };
    default:
      return { background: "#e2e8f0", color: "#334155" };
  }
};

const fmt = (v) => (v == null || v === "" ? "-" : v);
const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("en-IN");
};

function DetailModal({ row, approvalNotes, onNotesChange, onClose, onRequestApproval, onDispatch, busyId, dispatchPieces, onDispatchPiecesChange }) {
  if (!row) return null;
  const approval = row.dispatch_approval;
  const pendingAction = busyId === row.id;
  const availablePieces = row.available_pieces || row.quantity || 0;
  const orderedPieces = row.ordered_quantity || row.quantity || 0;

  return (
    <div className="dq-modal-overlay" onClick={onClose}>
      <div className="dq-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dq-modal-head">
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{row.design_name || row.design_code}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Job Card: JC-{row.job_card_id} | Customer: {row.customer_name || "-"}</div>
          </div>
          <button className="dq-btn" onClick={onClose}>Close</button>
        </div>
        <div className="dq-modal-body">
          <div className="dq-grid">
            <div className="dq-card">
              <h4>Product Details</h4>
              <div className="dq-row"><span>Design Code</span><strong>{fmt(row.design_code)}</strong></div>
              <div className="dq-row"><span>Quantity</span><strong>{fmt(row.quantity)}</strong></div>
              <div className="dq-row"><span>Available Pieces</span><strong>{fmt(row.available_pieces)}</strong></div>
              <div className="dq-row"><span>Ordered Quantity</span><strong>{fmt(row.ordered_quantity)}</strong></div>
              <div className="dq-row"><span>Already Dispatched</span><strong>{fmt(row.dispatched_pieces)}</strong></div>
              <div className="dq-row"><span>Final Weight</span><strong>{fmt(row.final_weight_g)} g</strong></div>
              <div className="dq-row"><span>QC Grade</span><strong>{fmt(row.qc_grade)}</strong></div>
              <div className="dq-row"><span>Hallmark</span><strong>{fmt(row.hallmark)}</strong></div>
              <div className="dq-row"><span>Certificate</span><strong>{fmt(row.certificate_no)}</strong></div>
              <div className="dq-row"><span>Delivery Status</span><span className="dq-badge" style={badgeStyle(row.delivery_status)}>{fmt(row.delivery_status)}</span></div>
            </div>
            <div className="dq-card">
              <h4>Customer Details</h4>
              <div className="dq-row"><span>Name</span><strong>{fmt(row.customer_name)}</strong></div>
              <div className="dq-row"><span>Phone</span><strong>{fmt(row.customer_phone)}</strong></div>
              <div className="dq-row"><span>Email</span><strong>{fmt(row.customer_email)}</strong></div>
              <div className="dq-row"><span>QC Passed At</span><strong>{fmtDate(row.qc_passed_at)}</strong></div>
              <div className="dq-row"><span>Moved To Inventory</span><strong>{fmtDate(row.moved_at)}</strong></div>
            </div>
            <div className="dq-card">
              <h4>Packing Summary</h4>
              <div className="dq-row"><span>Packed Qty</span><strong>{fmt(row.packing_summary?.packed_quantity)}</strong></div>
              <div className="dq-row"><span>Packing Type</span><strong>{fmt(row.packing_summary?.packing_type)}</strong></div>
              <div className="dq-row"><span>Packaging Material</span><strong>{fmt(row.packing_summary?.packaging_material)}</strong></div>
              <div className="dq-row"><span>Seal No</span><strong>{fmt(row.packing_summary?.seal_number)}</strong></div>
              <div className="dq-row"><span>Batch No</span><strong>{fmt(row.packing_summary?.batch_number)}</strong></div>
              <div className="dq-row"><span>Packed At</span><strong>{fmtDate(row.packing_summary?.packed_at)}</strong></div>
            </div>
            <div className="dq-card">
              <h4>Dispatch Approval</h4>
              <div className="dq-row"><span>Status</span><span className="dq-badge" style={badgeStyle(approval?.status || "NOT_REQUESTED")}>{approval?.status || "NOT_REQUESTED"}</span></div>
              <div className="dq-row"><span>Requested By</span><strong>{fmt(approval?.requested_by_name)}</strong></div>
              <div className="dq-row"><span>Requested At</span><strong>{fmtDate(approval?.requested_at)}</strong></div>
              <div className="dq-row"><span>Approved By</span><strong>{fmt(approval?.approved_by_name)}</strong></div>
              <div className="dq-row"><span>Approved At</span><strong>{fmtDate(approval?.approved_at)}</strong></div>
              <div className="dq-row"><span>Admin Notes</span><strong>{fmt(approval?.admin_notes)}</strong></div>
            </div>
          </div>

          {!approval || approval.status === "REJECTED" ? (
            <div className="dq-notes">
              <textarea
                className="dq-textarea"
                rows={3}
                placeholder="Add dispatch reason / notes for admin approval"
                value={approvalNotes}
                onChange={(e) => onNotesChange(e.target.value)}
              />
              <div className="dq-actions">
                <button className="dq-btn dq-btn-warning" disabled={pendingAction} onClick={onRequestApproval}>
                  {pendingAction ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Request Admin Approval
                </button>
              </div>
            </div>
          ) : null}

          {approval?.status === "APPROVED" || row.delivery_status === "DISPATCH_APPROVED" ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label className="dq-inline-label">Pieces to Dispatch</label>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                  Available: {availablePieces} pcs | Ordered: {orderedPieces} pcs
                </div>
                <input
                  className="dq-select"
                  type="number"
                  value={dispatchPieces}
                  min={1}
                  max={availablePieces}
                  onChange={(e) => onDispatchPiecesChange(Math.min(availablePieces, parseInt(e.target.value, 10) || 0))}
                />
              </div>
              <div className="dq-actions">
                <button className="dq-btn dq-btn-success" disabled={pendingAction || dispatchPieces <= 0} onClick={onDispatch}>
                  {pendingAction ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Dispatch Product
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function DispatchQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ACTIVE");
  const [selected, setSelected] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [dispatchPieces, setDispatchPieces] = useState(0);
  const [logisticsRow, setLogisticsRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authApi.get(`/sales/store-inventory/dispatch-queue?status=${filter}`);
      setRows(res.data?.data || []);
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load dispatch queue.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => [row.design_name, row.design_code, row.customer_name, `jc-${row.job_card_id}`].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [rows, query]);

  const requestApproval = async (row) => {
    setBusyId(row.id);
    try {
      await authApi.post(`/sales/store-inventory/${row.id}/request-dispatch-approval`, { request_notes: approvalNotes });
      setApprovalNotes("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to request approval.");
    } finally {
      setBusyId(null);
    }
  };

  const dispatchItem = async (row) => {
    setLogisticsRow(row);
  };

  const confirmDispatchWithLogistics = async (payload) => {
    if (!logisticsRow) return;
    setBusyId(logisticsRow.id);
    try {
      const dispatchMode = logisticsRow.stock_entry_mode === "BOXES" && Array.isArray(logisticsRow.box_details) && logisticsRow.box_details.length
        ? "BOXES"
        : "PIECES";
      const selectedBoxes = dispatchMode === "BOXES"
        ? logisticsRow.box_details.filter((box) => Number(box?.available_quantity || 0) > 0).map((box) => box.barcode)
        : undefined;

      const createRes = await authApi.post("/dispatch-notes/create", {
        customer_id: Number(logisticsRow.customer_id),
        sales_order_id: logisticsRow.sales_order_id ? Number(logisticsRow.sales_order_id) : undefined,
        remarks: payload.dispatch_notes || undefined,
        items: [{
          store_inventory_id: Number(logisticsRow.id),
          pieces: dispatchMode === "PIECES" ? Number(dispatchPieces) : undefined,
          unit_price: Number(logisticsRow.unit_cost || 0),
          dispatch_mode: dispatchMode,
          selected_boxes: dispatchMode === "BOXES" ? selectedBoxes : undefined,
        }],
      });
      const noteId = createRes?.data?.data?.id;
      const confirmRes = await authApi.put(`/dispatch-notes/${noteId}/confirm`, payload);

      const labelUrl = confirmRes?.data?.label_pdf_url;
      if (labelUrl) {
        const backendRoot = (process.env.REACT_APP_API_URL || "http://127.0.0.1:5011/api/v1").replace(/\/api\/v1\/?$/, "");
        const pdfRes = await authApi.get(`${backendRoot}${labelUrl}`, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `shipping_label_${createRes?.data?.data?.dispatch_number || noteId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }

      setSelected(null);
      setLogisticsRow(null);
      setDispatchPieces(0);
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to dispatch product."));
      throw err;
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="dq-wrap">
        <div className="dq-head">
          <div>
            <h2 className="dq-title">Dispatch Queue</h2>
            <div className="dq-sub">Finished products in inventory waiting for admin approval and dispatch.</div>
          </div>
          <button className="dq-btn" onClick={load}><RefreshCw size={14} style={{ marginRight: 6 }} />Refresh</button>
        </div>

        <div className="dq-tools">
          <div className="dq-search">
            <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "#94a3b8" }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search design, customer or job card" />
          </div>
          <select className="dq-select" style={{ maxWidth: 180 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="ACTIVE">Active Queue</option>
            <option value="DISPATCHED">Dispatched</option>
            <option value="ALL">All</option>
          </select>
        </div>

        {loading ? <div className="dq-empty"><Loader2 size={18} className="animate-spin" /> Loading dispatch queue...</div> :
          error ? <div className="dq-empty" style={{ color: "#b91c1c" }}>{error}</div> :
            filtered.length === 0 ? <div className="dq-empty">No products found in this dispatch view.</div> : (
              <div className="dq-table-wrap">
                <table className="dq-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Customer</th>
                      <th>Qty</th>
                      <th>Available Pcs</th>
                      <th>Ordered</th>
                      <th>Weight</th>
                      <th>QC Grade</th>
                      <th>Approval</th>
                      <th>Delivery Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div style={{ fontWeight: 800 }}>{row.design_name || row.design_code}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>JC-{row.job_card_id}</div>
                          {row.is_old_stock ? <div style={{ marginTop: 6 }}><span className="dq-badge" style={{ background: "#fef3c7", color: "#92400e" }}>Old Stock</span></div> : null}
                        </td>
                        <td><div>{row.customer_name || "-"}</div><div style={{ fontSize: 11, color: "#64748b" }}>{row.customer_phone || "-"}</div></td>
                        <td>{row.quantity}</td>
                        <td>{row.available_pieces ?? "-"}</td>
                        <td>{row.ordered_quantity ?? "-"}</td>
                        <td>{fmt(row.final_weight_g)} g</td>
                        <td>{row.qc_grade || "-"}</td>
                        <td><span className="dq-badge" style={badgeStyle(row.dispatch_approval?.status || "NOT_REQUESTED")}>{row.dispatch_approval?.status || "NOT_REQUESTED"}</span></td>
                        <td><span className="dq-badge" style={badgeStyle(row.delivery_status)}>{row.delivery_status || "-"}</span></td>
                        <td>
                          <div className="dq-actions">
                            <button className="dq-btn" onClick={() => {
                              setSelected(row);
                              setApprovalNotes(row.dispatch_approval?.request_notes || "");
                              setDispatchPieces(Math.min(row.available_pieces || row.quantity || 0, row.ordered_quantity || row.available_pieces || row.quantity || 0));
                            }}><Eye size={14} /> Details</button>
                            {(!row.dispatch_approval || row.dispatch_approval.status === "REJECTED") && row.delivery_status !== "DISPATCHED" ? (
                              <button className="dq-btn dq-btn-warning" disabled={busyId === row.id} onClick={() => requestApproval(row)}>
                                {busyId === row.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Ask Approval
                              </button>
                            ) : null}
                            {(row.dispatch_approval?.status === "APPROVED" || row.delivery_status === "DISPATCH_APPROVED") && row.delivery_status !== "DISPATCHED" ? (
                              <button className="dq-btn dq-btn-success" disabled={busyId === row.id} onClick={() => dispatchItem(row)}>
                                {busyId === row.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Dispatch
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
      <DetailModal
        row={selected}
        approvalNotes={approvalNotes}
        onNotesChange={setApprovalNotes}
        onClose={() => setSelected(null)}
        onRequestApproval={() => selected && requestApproval(selected)}
        onDispatch={() => selected && dispatchItem(selected)}
        busyId={busyId}
        dispatchPieces={dispatchPieces}
        onDispatchPiecesChange={setDispatchPieces}
      />
      <LogisticsModal
        open={Boolean(logisticsRow)}
        onClose={() => setLogisticsRow(null)}
        onConfirm={confirmDispatchWithLogistics}
        totalWeightKg={Number(logisticsRow?.total_weight || logisticsRow?.final_weight_g || 0) / 1000}
        row={logisticsRow}
      />
    </>
  );
}
